import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Edgemarc from '../../pages/tabs/edgemarc/Edgemarc.jsx';
import Dispatch from '../../pages/tabs/Dispatch.jsx';
import FieldEngineer from '../../pages/tabs/FieldEngineer.jsx';
import Folder from '../../pages/tabs/Folder.jsx';
import Email from '../../pages/tabs/Email.jsx';
import Fax from '../../pages/tabs/Fax.jsx';
import Whois from '../../pages/tabs/Whois.jsx';
import Tabs from '../../pages/tabs/Tabs.jsx';
import { tabService } from '../../services';
import TabEditModal from './TabEditModal';
import './ChromeTabs.css';

const ChromeTabs = () => {
  const { id: ticketId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [customTabNames, setCustomTabNames] = useState({});
  const [customTabColors, setCustomTabColors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTabId, setEditingTabId] = useState(null);

  // Define tab data structure with component types and default colors
  const tabData = useMemo(() => [
    {
      id: 0,
      title: 'Quick copy',
      componentType: Email,
      bgcolor: 'bg-blue-300',
      nocolor: 'bg-blue-100',
      hovercolor: 'hover:bg-blue-200',
    },
    {
      id: 1,
      title: 'Folder',
      componentType: Folder,
      bgcolor: 'bg-purple-300',
      nocolor: 'bg-purple-100',
      hovercolor: 'hover:bg-purple-200',
    },
    {
      id: 2,
      title: 'Edgemarc',
      componentType: Edgemarc,
      bgcolor: 'bg-red-300',
      nocolor: 'bg-red-100',
      hovercolor: 'hover:bg-red-200',
    },
    {
      id: 3,
      title: 'eFax',
      componentType: Fax,
      bgcolor: 'bg-pink-300',
      nocolor: 'bg-pink-100',
      hovercolor: 'hover:bg-pink-200',
    },
    {
      id: 4,
      title: 'Whois',
      componentType: Whois,
      bgcolor: 'bg-yellow-300',
      nocolor: 'bg-yellow-100',
      hovercolor: 'hover:bg-yellow-200',
    },
    {
      id: 5,
      title: 'Dispatch',
      componentType: Dispatch,
      bgcolor: 'bg-greenDispatch',
      nocolor: 'bg-green-100',
      hovercolor: 'hover:bg-green-200',
    },
    {
      id: 6,
      title: 'Field Engineer',
      componentType: FieldEngineer,
      bgcolor: 'bg-orangeFieldEngineer',
      nocolor: 'bg-orange-100',
      hovercolor: 'hover:bg-orange-200',
    },
    {
      id: 7,
      title: 'Tab 1',
      componentType: Tabs,
      tabId: 7,
      bgcolor: 'bg-orange-300',
      nocolor: 'bg-orange-100',
      hovercolor: 'hover:bg-orange-200',
    },
    {
      id: 8,
      title: 'Tab 2',
      componentType: Tabs,
      tabId: 8,
      bgcolor: 'bg-orange-300',
      nocolor: 'bg-orange-100',
      hovercolor: 'hover:bg-orange-200',
    },
    {
      id: 9,
      title: 'Tab 3',
      componentType: Tabs,
      tabId: 9,
      bgcolor: 'bg-orange-300',
      nocolor: 'bg-orange-100',
      hovercolor: 'hover:bg-orange-200',
    },
  ], []);

  useEffect(() => {
    const fetchCustomTabData = async () => {
      if (!ticketId) return;

      try {
        const data = await tabService.getCustomTabNames(ticketId);
        const tabNamesMap = {};
        const tabColorsMap = {};

        data.forEach(item => {
          tabNamesMap[item.tab_number] = item.custom_name;

          if (item.bgcolor && item.nocolor && item.hovercolor) {
            tabColorsMap[item.tab_number] = {
              bgcolor: item.bgcolor,
              nocolor: item.nocolor,
              hovercolor: item.hovercolor
            };
          }
        });

        setCustomTabNames(tabNamesMap);
        setCustomTabColors(tabColorsMap);
      } catch (error) {
        console.error('Error fetching custom tab data:', error);
      }
    };
    fetchCustomTabData();
  }, [ticketId]);

  const getTabTitle = (id, defaultTitle) => {
    // For tabs 7, 8, 9 (Tab 1, Tab 2, Tab 3), use tab numbers 1, 2, 3
    if (id >= 7 && id <= 9) {
      const tabNumber = id - 6; // 7->1, 8->2, 9->3
      return customTabNames[tabNumber] || defaultTitle;
    }
    return defaultTitle;
  };

  const getTabColors = (id) => {
    if (id >= 7 && id <= 9) {
      const tabNumber = id - 6; // 7->1, 8->2, 9->3
      const customColors = customTabColors[tabNumber];

      if (customColors) {
        return customColors;
      }
    }

    // Return default colors from tabData
    const tab = tabData.find(t => t.id === id);
    return {
      bgcolor: tab?.bgcolor,
      nocolor: tab?.nocolor,
      hovercolor: tab?.hovercolor
    };
  };

  // Convert Tailwind color class to hex value for CSS variable
  const getColorHex = (bgColorClass) => {
    const colorMap = {
      'bg-blue-300': '#93c5fd',
      'bg-purple-300': '#d8b4fe',
      'bg-red-300': '#fca5a5',
      'bg-pink-300': '#f9a8d4',
      'bg-yellow-300': '#fde047',
      'bg-green-300': '#86efac',
      'bg-orange-300': '#fdba74',
      'bg-greenDispatch': '#10b981',
      'bg-orangeFieldEngineer': '#f97316',
    };
    return colorMap[bgColorClass] || '#93c5fd';
  };

  // Get CSS class name for tab color
  const getTabColorClass = (bgColorClass) => {
    // Convert bg-color-### to tab-color-color-###
    return bgColorClass.replace('bg-', 'tab-color-');
  };

  // Sync active tab color with CSS variable
  useEffect(() => {
    const colors = getTabColors(activeTab);
    const hexColor = getColorHex(colors.bgcolor);
    document.documentElement.style.setProperty('--active-color', hexColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, customTabColors]);

  const handleEditClick = (id) => {
    if (id >= 7 && id <= 9) {
      setEditingTabId(id);
      setIsModalOpen(true);
    }
  };

  const handleModalSave = async (name, colorData) => {
    if (editingTabId && editingTabId >= 7 && editingTabId <= 9 && ticketId) {
      const tabNumber = editingTabId - 6; // 7->1, 8->2, 9->3

      try {
        await tabService.updateCustomTabName(
          ticketId,
          tabNumber,
          name,
          {
            bgcolor: colorData.bgcolor,
            nocolor: colorData.nocolor,
            hovercolor: colorData.hovercolor
          }
        );

        setCustomTabNames(prev => ({
          ...prev,
          [tabNumber]: name
        }));

        setCustomTabColors(prev => ({
          ...prev,
          [tabNumber]: {
            bgcolor: colorData.bgcolor,
            nocolor: colorData.nocolor,
            hovercolor: colorData.hovercolor
          }
        }));
      } catch (error) {
        console.error('Error updating tab:', error);
      }
    }
  };

  // Render active tab component with color props
  const renderActiveTabContent = () => {
    const activeTabData = tabData.find(({ id }) => id === activeTab);
    if (!activeTabData) return 'No content available';

    const Component = activeTabData.componentType;
    const colors = getTabColors(activeTab);

    // Pass tabId and colors to the component
    if (activeTabData.tabId !== undefined) {
      return <Component tabId={activeTabData.tabId} tabColors={colors} />;
    }
    return <Component tabColors={colors} />;
  };

  return (
    <div className="chrome-tabs-container">
      <div className="chrome-tab-strip">
        {tabData.map(({ id, title }) => {
          const displayTitle = getTabTitle(id, title);
          const isCustomizable = id >= 7 && id <= 9;
          const colors = getTabColors(id);
          const tabColorClass = getTabColorClass(colors.nocolor);

          return (
            <div
              key={id}
              className={`chrome-tab ${activeTab === id ? 'chrome-tab-active' : ''} ${tabColorClass}`}
              data-label={displayTitle}
              onClick={() => setActiveTab(id)}
            >
              {activeTab === id && (
                <div className="chrome-tab-hump">
                  <span>{displayTitle}</span>
                  {isCustomizable && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="chrome-tab-edit-icon"
                      style={{ opacity: 1, marginLeft: '0.5rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(id);
                      }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                      />
                    </svg>
                  )}
                </div>
              )}
              <div className="chrome-tab-content">
                <span>{displayTitle}</span>
                {isCustomizable && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="chrome-tab-edit-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(id);
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                    />
                  </svg>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="chrome-tab-content-area">
        {renderActiveTabContent()}
      </div>

      {/* Tab Edit Modal */}
      {isModalOpen && editingTabId && (
        <TabEditModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTabId(null);
          }}
          tabName={getTabTitle(editingTabId, '')}
          tabColor={getTabColors(editingTabId)}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default ChromeTabs;
